import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "../translations";
import { API_BASE } from '../config';
import { DEMO_DISABLED_TITLE } from '../utils/demoBanner';
import { Modal } from '../components/ui/Modal';
import { Banner } from '../components/ui/Banner';
import { IconButton, panelTopHairline } from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';

// SVG Icons
const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="3,6 5,6 21,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 32px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: var(--shadow-md);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 48px);
  
  ${panelTopHairline}

  @media (min-width: 1400px) {
    max-width: 1600px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
`;


const Title = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--text);
  letter-spacing: -0.025em;

`;

const PrimaryButton = styled(Link)`
  background: var(--primary);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }
`;

const DisabledPrimaryButton = styled.span`
  background: var(--text-muted);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.7;
`;

const Panel = styled.div`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideIn} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

// Tresc modali potwierdzenia (usuniecie / wylogowanie urzadzen) — sam modal to
// wspolny <Modal> z components/ui/Modal.js, tu zostaje tylko wnetrze.
const ModalBody = styled.div`
  color: var(--text);
  font-size: 0.95rem;
  line-height: 1.35;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 2px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text);
  outline: none;

  &:focus {
    border-color: var(--primary);
  }
`;

const SecondaryButton = styled.button`
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--table-row-hover);
    border-color: var(--primary);
    color: var(--primary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  background: var(--danger);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const UserTable = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--input-border);
`;

const UserHeader = styled.div`
  display: grid;
  /* minmax(0, Nfr), nie goły Nfr: UserHeader i UserRow to OSOBNE kontenery grid (każdy wiersz
     liczy własne szerokości kolumn), więc bez ograniczenia dolnego długi e-mail/imię w jednym
     wierszu rozpycha jego kolumny 1-3 inaczej niż w sąsiednim wierszu czy nagłówku — kolumny
     4 (Admin) i 5 (Akcje) rozjeżdżały się w pionie między wierszami. Kolumna 5 dostaje STAŁĄ
     szerokość zamiast auto z tego samego powodu: auto liczy się z zawartości TEGO konkretnego
     grida, a w nagłówku zawartością jest sam napis "Akcje" (węższy niż 3 przyciski akcji
     w wierszu) — bez stałej wartości szerokość tej kolumny i tak by się rozjeżdżała między
     nagłówkiem a wierszami, nawet po ograniczeniu kolumn 1-3. */
  grid-template-columns: minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2.5fr) 1fr 112px;
  gap: 16px;
  padding: 16px 20px;
  background: var(--panel-bg);
  font-weight: 700;
  color: var(--text);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  & > div {
    display: flex;
    align-items: center;
    padding: 0;
  }

  /* Left-align first 3 columns */
  & > div:nth-child(1),
  & > div:nth-child(2),
  & > div:nth-child(3) {
    justify-content: flex-start;
  }

  /* Center "ADMINISTRATOR" column (4th) and add left padding */
  & > div:nth-child(4) {
    justify-content: center;
    padding-left: 24px;
  }

  /* Center "AKCJE" column (5th) */
  & > div:nth-child(5) {
    justify-content: center;
  }

  @media(max-width: 768px) {
    grid-template-columns: 1fr auto;
    gap: 8px;
  }
`;

const UserRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2.5fr) 1fr 112px;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--input-border);
  background: var(--card-bg);
  transition: all 0.2s ease;
  align-items: center;
  
  &:hover {
    background: var(--table-row-hover);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media(max-width: 768px) {
    grid-template-columns: 1fr auto;
    gap: 8px;
  }
`;

const UserCell = styled.div`
  font-size: 0.9rem;
  color: var(--text);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  /* Pozwala flex-childom (tekst imienia/e-maila) skurczyć się poniżej swojej naturalnej
     szerokości, żeby ellipsis niżej mógł zadziałać zamiast rozpychać kolumnę 1fr/2fr. */
  min-width: 0;

  svg { flex-shrink: 0; }

  /* Wyśrodkuj 4. kolumnę (admin badge) i dodaj padding-left */
  &:nth-child(4) {
    justify-content: center;
    padding-left: 24px;
  }
  
  /* Wyśrodkuj 5. kolumnę (akcje) */
  &:nth-child(5) {
    justify-content: center;
  }
  
  @media(max-width: 768px) {
    &:nth-child(n+3):nth-child(-n+4) {
      display: none;
    }
  }
`;

const Truncate = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileUserInfo = styled.div`
  display: none;
  flex-direction: column;
  gap: 4px;
  
  @media(max-width: 768px) {
    display: flex;
  }
`;

const AdminBadge = styled.span`
  background: var(--success);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(Link)`
  background: ${props => props.danger ? 'var(--danger-bg)' : 'var(--info-bg)'};
  color: ${props => props.danger ? 'var(--danger)' : 'var(--info)'};
  border: 1px solid ${props => props.danger ? 'var(--danger-border)' : 'var(--info-border)'};
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;

  &:hover {
    filter: brightness(0.95);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }

  &:disabled:hover {
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--text-muted);
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.7;
    color: var(--text-muted);
  }
  
  .text {
    font-size: 1.1rem;
    font-weight: 500;
  }
`;

export default function Users() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDemo = localStorage.getItem('isDemo') === '1';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [notice, setNotice] = useState(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeError, setRevokeError] = useState(null);
  const [revokeProcessing, setRevokeProcessing] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  function getCookie(name){
    if(typeof document === 'undefined') return '';
    const v = document.cookie.split(';').map(c=>c.trim()).find(c=>c.startsWith(name+'='));
    return v ? decodeURIComponent(v.split('=').slice(1).join('=')) : '';
  }

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/api/users`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Network response was not ok`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          setUsers(data);
        } else if (data && typeof data === 'object' && Array.isArray(data.users)) {
          setUsers(data.users);
        } else {
          setUsers([]);
          setError('Nieprawidłowy format danych z serwera');
        }
        
      } catch (err) {
        setError(`${t('users.loadError')}: ${err.message}`);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [t]);

  useEffect(() => {
    let mounted = true;
    const fetchWhoami = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/whoami`, {
          method: 'GET',
          credentials: 'include'
        });
        if (!mounted) return;
        if (!res.ok) return;
        const j = await res.json().catch(() => ({}));
        const id = j.userId ?? j.id ?? j.user?.id;
        if (typeof id !== 'undefined' && id !== null && id !== '') {
          setCurrentUserId(Number(id));
        }
      } catch {
        // ignoruj
      }
    };
    fetchWhoami();
    return () => { mounted = false; };
  }, []);

  // Odcięcie dostępu pracownikowi. Świadomie BEZ pytania o hasło admina, inaczej niż przy usuwaniu
  // konta: ta operacja jest odwracalna (pracownik po prostu loguje się ponownie), a przy zwolnieniu
  // liczy się, żeby dało się ją zrobić od razu.
  const openRevokeModal = (user) => {
    setRevokeTarget(user);
    setRevokeError(null);
    setShowRevokeModal(true);
  };

  const closeRevokeModal = () => {
    if (revokeProcessing) return;
    setShowRevokeModal(false);
    setRevokeTarget(null);
    setRevokeError(null);
  };

  const confirmRevokeSessions = async () => {
    if (!revokeTarget?.id) return;
    setRevokeProcessing(true);
    setRevokeError(null);
    try {
      const csrf = getCookie('csrf') || (typeof localStorage !== 'undefined' ? localStorage.getItem('csrfToken') : '') || '';
      const response = await fetch(`${API_BASE}/api/security/sessions/user/${revokeTarget.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        }
      });
      if (!response.ok) throw new Error('failed');
      const data = await response.json().catch(() => ({}));
      setShowRevokeModal(false);
      setRevokeTarget(null);
      setNotice(t('users.revokeDone').replace('{count}', String(data.revoked ?? 0)));
    } catch (e) {
      setRevokeError(t('users.revokeFailed'));
    } finally {
      setRevokeProcessing(false);
    }
  };

  const openDeleteModal = (user) => {
    setDeleteTarget(user);
    setDeletePassword('');
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleteProcessing) return;
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setDeletePassword('');
    setDeleteError(null);
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget?.id) return;
    if (!deletePassword) {
      setDeleteError(t('users.enterPasswordError'));
      return;
    }
    setDeleteProcessing(true);
    setDeleteError(null);
    try {
      const csrf = getCookie('csrf') || (typeof localStorage !== 'undefined' ? localStorage.getItem('csrfToken') : '') || '';
      const response = await fetch(`${API_BASE}/api/users/${deleteTarget.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify({ password: deletePassword })
      });

      if (!response.ok) {
        const bodyText = await response.text().catch(() => null);
        let parsed = null;
        try { parsed = bodyText ? JSON.parse(bodyText) : null; } catch(_) {}

        if (response.status === 401) {
          setDeleteError(t('users.wrongPasswordError'));
          return;
        }
        if (response.status === 400 && parsed && /delete yourself/i.test(parsed.message || '')) {
          setDeleteError(t('users.cannotDeleteSelfError'));
          return;
        }
        if (response.status === 400 && parsed && /last administrator/i.test(parsed.message || '')) {
          setDeleteError(t('users.lastAdminError'));
          return;
        }

        setDeleteError(parsed?.message || bodyText || t('users.serverError', { status: response.status }));
        return;
      }

      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      closeDeleteModal();
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteError(t('users.deleteError') || 'Błąd podczas usuwania użytkownika');
    } finally {
      setDeleteProcessing(false);
    }
  };

  return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
          <ArrowLeftIcon />
        </IconButton>
        <TitleSection>
          <Title>
            <UsersIcon />
            {t('users.title') || 'Użytkownicy'}
          </Title>
        </TitleSection>
        {isDemo ? (
          <DisabledPrimaryButton title={DEMO_DISABLED_TITLE}>
            <PlusIcon />
            {t('users.addUser') || 'Dodaj użytkownika'}
          </DisabledPrimaryButton>
        ) : (
          <PrimaryButton to="/add-user">
            <PlusIcon />
            {t('users.addUser') || 'Dodaj użytkownika'}
          </PrimaryButton>
        )}
      </Header>

      {loading && (
        <div style={{ marginBottom: 16 }}><Banner tone="info">{t('common.loading')}</Banner></div>
      )}

      {error && (
        <div style={{ marginBottom: 16 }}><Banner tone="danger">{error}</Banner></div>
      )}

      {notice && (
        <div style={{ marginBottom: 16 }}><Banner tone="success">{notice}</Banner></div>
      )}

      {!loading && !error && (
        <Panel>
          {users.length > 0 ? (
            <UserTable>
              <UserHeader>
                <div>{t('users.firstName')}</div>
                <div>{t('users.lastName')}</div>
                <div>{t('users.email')}</div>
                <div>{t('users.admin')}</div>
                <div>{t('common.actions')}</div>
              </UserHeader>
              
              {users.map(user => (
                <UserRow key={user.id}>
                  <UserCell>
                    <UserIcon />
                    <div style={{ minWidth: 0 }}>
                      <Truncate>{user.firstname || t('users.noFirstName')}</Truncate>
                      <MobileUserInfo>
                        <small style={{ color: 'var(--text-muted)' }}>{user.lastname || t('users.noLastName')}</small>
                        <small style={{ color: 'var(--text-muted)' }}>{user.email || t('users.noEmail')}</small>
                      </MobileUserInfo>
                    </div>
                  </UserCell>
                  <UserCell><Truncate>{user.lastname || t('users.noLastName')}</Truncate></UserCell>
                  <UserCell>
                    <MailIcon />
                    <Truncate>{user.email || t('users.noEmail')}</Truncate>
                  </UserCell>
                  <UserCell>
                    {(user.isadmin || user.is_admin) ? <AdminBadge>Admin</AdminBadge> : '-'}
                  </UserCell>
                  <UserCell>
                    <ActionButtons>
                      <ActionButton to={`/users/${user.id}`} title="Edytuj">
                        <EditIcon />
                      </ActionButton>
                      {/* Na wlasnym wierszu zablokowany, wzorem przycisku usuwania obok. Backend i tak
                          oszczedza biezaca sesje wolajacego, wiec przycisk tutaj nie zrobilby tego, co
                          obiecuje modal; od wylogowania wlasnych urzadzen jest ekran Bezpieczenstwo konta. */}
                      <ActionButton
                        as="button"
                        onClick={() => openRevokeModal(user)}
                        disabled={currentUserId && Number(user.id) === Number(currentUserId)}
                        title={currentUserId && Number(user.id) === Number(currentUserId)
                          ? t('users.revokeSelfHint')
                          : t('users.revokeSessions')}
                        aria-label={`${t('users.revokeSessions')}: ${user.firstname || ''} ${user.lastname || ''}`.trim()}
                        style={{ border: 'none' }}
                      >
                        <LogoutIcon />
                      </ActionButton>
                      <ActionButton
                        as="button"
                        danger
                        onClick={() => openDeleteModal(user)}
                        title={currentUserId && Number(user.id) === Number(currentUserId) ? t('users.cannotDeleteSelfError') : t('common.delete')}
                        aria-label={`${t('common.delete')}: ${user.firstname || ''} ${user.lastname || ''}`.trim()}
                        disabled={currentUserId && Number(user.id) === Number(currentUserId)}
                        style={{border: 'none'}}
                      >
                        <TrashIcon />
                      </ActionButton>
                    </ActionButtons>
                  </UserCell>
                </UserRow>
              ))}
            </UserTable>
          ) : (
            <EmptyState>
              <div className="icon">
                <UsersIcon />
              </div>
              <div className="text">{t('users.noUsers')}</div>
            </EmptyState>
          )}
        </Panel>
      )}

      <Modal
        open={showDeleteModal}
        onClose={closeDeleteModal}
        dismissOnBackdrop={!deleteProcessing}
        title={t('users.deleteModalTitle')}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={closeDeleteModal} disabled={deleteProcessing}>
              {t('common.cancel')}
            </SecondaryButton>
            <DangerButton onClick={confirmDeleteUser} disabled={deleteProcessing}>
              {deleteProcessing ? '...' : t('common.delete')}
            </DangerButton>
          </>
        }
      >
        <ModalBody>
          <div>{t('users.confirmDelete')}</div>
          <div style={{ marginTop: 10, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            {t('users.user')}: <strong>{deleteTarget?.firstname} {deleteTarget?.lastname}</strong>
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.9rem', marginBottom: 6 }}>{t('users.passwordConfirmLabel')}</div>
            <ModalInput
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder={t('users.passwordPlaceholder')}
              autoFocus
            />
            {deleteError && (
              <div style={{ marginTop: 8 }}><Banner tone="danger" compact>{deleteError}</Banner></div>
            )}
          </div>
        </ModalBody>
      </Modal>

      <Modal
        open={showRevokeModal}
        onClose={closeRevokeModal}
        dismissOnBackdrop={!revokeProcessing}
        title={t('users.revokeSessions')}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={closeRevokeModal} disabled={revokeProcessing}>
              {t('common.cancel')}
            </SecondaryButton>
            <DangerButton onClick={confirmRevokeSessions} disabled={revokeProcessing}>
              {revokeProcessing ? '...' : t('users.revokeAction')}
            </DangerButton>
          </>
        }
      >
        <ModalBody>
          <div>{t('users.revokeConfirm')}</div>
          <div style={{ marginTop: 10, fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            {t('users.user')}: <strong>{revokeTarget?.firstname} {revokeTarget?.lastname}</strong>
          </div>
          {revokeError && (
            <div style={{ marginTop: 8 }}><Banner tone="danger" compact>{revokeError}</Banner></div>
          )}
        </ModalBody>
      </Modal>
    </Container>
  );
}